import { useState, useMemo } from 'react';
import { Task, TaskStatus, ViewId } from './types';
import { ChatInput } from './components/ChatInput';
import { Sidebar } from './components/Sidebar';
import { KanbanBoard } from './components/KanbanBoard';
import { ProjectFilter } from './components/ProjectFilter';
import { Settings } from './components/Settings';
import { TaskDetailPanel } from './components/TaskDetailPanel';
import { useTasks, filterTasks, useTaskCounts } from './hooks/useTasks';

export function App() {
  const [view, setView] = useState<ViewId>('all');
  const [activeProject, setActiveProject] = useState<string | null>(null);
  const [checkingReplies, setCheckingReplies] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  const { tasks, loading, addTask, updateTask, deleteTask, reorderTasks, reload } = useTasks();
  const counts = useTaskCounts(tasks);

  const filtered = useMemo(
    () => filterTasks(tasks, view, activeProject),
    [tasks, view, activeProject],
  );

  // Always derive selectedTask from live tasks array so panel shows fresh data
  const selectedTask = selectedTaskId ? (tasks.find((t) => t.id === selectedTaskId) ?? null) : null;

  function handleTaskCreated(task: Task) {
    addTask(task);
  }

  function handleComplete(id: string, completed: boolean) {
    const status: TaskStatus = completed ? 'completed' : 'not_started';
    updateTask(id, { completed, status });
  }

  function handleDueDateChange(id: string, dueDate: string | undefined) {
    updateTask(id, { dueDate });
  }

  function handleProjectChange(id: string, project: string | undefined) {
    updateTask(id, { project });
  }

  function handleDelete(id: string) {
    if (selectedTaskId === id) setSelectedTaskId(null);
    deleteTask(id);
  }

  function handleViewChange(v: ViewId) {
    setView(v);
    setActiveProject(null);
  }

  const cardCallbacks = {
    onComplete: handleComplete,
    onDelete: handleDelete,
    onDueDateChange: handleDueDateChange,
    onProjectChange: handleProjectChange,
    onSelect: (task: Task) => setSelectedTaskId(task.id),
  };

  return (
    <div className="h-screen flex flex-col bg-gray-950 text-white overflow-hidden">
      {/* Top bar */}
      <header className="shrink-0 border-b border-gray-800 px-6 py-3 flex items-center gap-4">
        <div className="flex items-center gap-2 mr-4 shrink-0">
          <span className="text-indigo-400 text-xl">✦</span>
          <span className="font-semibold text-white tracking-tight">TaskFlow</span>
        </div>
        <ChatInput onTaskCreated={handleTaskCreated} />
        {checkingReplies && (
          <span className="text-xs text-gray-500 animate-pulse shrink-0">checking replies…</span>
        )}
      </header>

      {/* Body */}
      <div className="flex-1 flex overflow-hidden">
        <Sidebar current={view} counts={counts} onNavigate={handleViewChange} />

        {view === 'settings' ? (
          <Settings onCheckingRepliesChange={setCheckingReplies} onImportComplete={reload} />
        ) : (
          <div className="flex-1 flex flex-col overflow-hidden">
            <ProjectFilter
              tasks={tasks}
              active={activeProject}
              onChange={setActiveProject}
            />

            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <span className="text-gray-600 text-sm">Loading…</span>
              </div>
            ) : (
              <div className="flex-1 overflow-hidden px-6 pt-4">
                <KanbanBoard
                  tasks={filtered}
                  onReorder={reorderTasks}
                  callbacks={cardCallbacks}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Task detail side panel */}
      <TaskDetailPanel
        task={selectedTask}
        onClose={() => setSelectedTaskId(null)}
        onUpdate={updateTask}
      />
    </div>
  );
}
